import React, { FC, useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { Row, Col, Form, Card, Button } from 'react-bootstrap'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import DateFnsUtils from '@date-io/date-fns'
import RootState from '../../../../types/RootState'
import { KeyboardTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
import LightBlueSwitch from '../../../LightBlueSwitch'
import MaskedInput from 'react-maskedinput'
import { Formik } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getWeekDay, sortArrayOfObjects } from '../../../../utils/dataTools'
import { ResidentialGeneralInfoSchema, NonResidentialGeneralInfoSchema } from '../../../../utils/validation'
import { queryAutocompleteForSuggestions, queryAutocompleteForEntries, generateAddressOption, validateUSAddress, formatAutocompleteSuggestion } from '../../../../utils/smarty'
import { sortBy, isEmpty, toLower, debounce } from 'lodash'

interface Props {
  activeStep: number;
  applicationType: string;
  initialValues: any;
  readonly: boolean;
  handleNext: (values: any) => void;
  onSubmit: (values: any) => void;
}

const formatOptions = (options: any, id: string, name?: string) => {
  return sortBy(
    options.map((option) => ({
      value: option[id],
      label: name ? option[name] : option.name
    })), 
    ((o) => o.label)
  )
}

const customStyles = {
  control: (base) => ({
    ...base,
    borderColor: 'red !important',
    boxShadow: 'none'
  }),
}

const GeneralInfo: FC<Props> = ({ activeStep, applicationType, initialValues, readonly, handleNext, onSubmit }: Props) => {
  const [copyMondayError, setCopyMondayError] = useState(false)
  const [copySaturdayError, setCopySaturdayError] = useState(false)
  const { serviceTypes, targetGroups, accessibilities, languages, counties, wavierCompliant, overnightCare, staffingModel, race, ethnicity } = useSelector((state: RootState) => ({
    counties: state.location.counties.data,
    accessibilities: state.accessibility.data,
    serviceTypes: state.serviceType.data,
    targetGroups: state.targetGroup.data,
    languages: state.language.data,
    wavierCompliant: state.wavierCompliant.data,
    overnightCare: state.overnightCare.data,
    staffingModel: state.staffingModel.data,
    race: state.race.data,
    ethnicity: state.ethnicity.data,
  }))

  // Smarty variables
  const [smartyError, setSmartyError] = useState(null)
  const [homeDefaultOptions, setHomeDefaultOptions]: any = useState([])
  const [billingDefaultOptions, setBillingDefaultOptions]: any = useState([])
  const [homeEntries, setHomeEntries]: any = useState([])
  const [billingEntries, setBillingEntries]: any = useState([])
  const [homeAddressSuggestions, setHomeAddressSuggestions]: any = useState([])
  const [billingAddressSuggestions, setBillingAddressSuggestions]: any = useState([])

  const homeInputElement = useRef(null)
  useEffect(() => {
    if (homeInputElement.current && homeEntries ) {
      homeInputElement.current.focus()
    }
  }, [homeEntries])

  const billingInputElement = useRef(null)
  useEffect(() => {
    if (billingInputElement.current && billingEntries ) {
      billingInputElement.current.focus()
    }
  }, [billingEntries])

  useEffect(() => {
    if ( initialValues ) {
      const homeDefault = generateAddressOption({ address1: initialValues.address1, address2: initialValues.address2, city: initialValues.city, state: initialValues.state, zipCode: initialValues.zipCode })
      const billingDefault = generateAddressOption({ address1: initialValues.billingAddress1, address2: initialValues.billingAddress2, city: initialValues.billingCity, state: initialValues.billingState, zipCode: initialValues.billingZipCode })

      setHomeDefaultOptions(homeDefault ? [homeDefault] : [])
      setBillingDefaultOptions(billingDefault ? [billingDefault] : [])
    }
  }, [initialValues]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckboxGroupChange = (key: string, value: any, values: any, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
    let newValues = values[key]
    if (!newValues.includes(value)) {
      newValues.push(value)
    } else {
      newValues = newValues.filter((newValue) => newValue !== value)
    }
    setFieldValue(key, newValues)
  }

  const handleCountyChange = (countyServed: boolean, values: any, counties: any, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void) => {
    setFieldValue('counties', values ? values.map((value: any) => value.value) : [])
  }

  const handleCopyToSome = (from: string, start: number, end: number, values, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void) => {
    if ((!values[`${from}Start`] || !values[`${from}End`]) && !values[`${from}NA`]) {
      if (from === 'monday') {
        setCopyMondayError(true)
      } else {
        setCopySaturdayError(true)
      }
    } else {
      if (from === 'monday') {
        setCopyMondayError(false)
      } else {
        setCopySaturdayError(false)
      }
      setFieldValue(from, true)
      setFieldTouched(from, true)
      for (let i = start; i < end; i++) {
        const weekDay = getWeekDay(i)

        if (values[`${from}NA`]) {
          setFieldValue(`${weekDay}NA`, values[`${from}NA`])
          setFieldValue(`${weekDay}Start`, null)
          setFieldValue(`${weekDay}End`, null)
          setFieldValue(weekDay || '', true)
        } else {
          setFieldValue(`${weekDay}Start`, values[`${from}Start`])
          setFieldValue(`${weekDay}End`, values[`${from}End`])
          setFieldValue(`${weekDay}NA`, false)
          setFieldValue(weekDay || '', true)
        }

        setTimeout(() => setFieldTouched(weekDay || '', true))
      }
    }
  }

  /** Smarty integration */
  // load options using API call
  const loadHomeOptions = (inputValue: string,
    callback: (options: any) => void
  ) => {
    queryAutocompleteForSuggestions(inputValue, setHomeAddressSuggestions, callback)
  }

  // load options using API call
  const loadBillingOptions = (inputValue: string,
    callback: (options: any) => void
  ) => {
    queryAutocompleteForSuggestions(inputValue, setBillingAddressSuggestions, callback)
  }
  
  const setSelectedOptions = (selected: any, type: string, setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void, setFieldTouched?: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void) => {
    const abbr = type.includes('Entries') ? `${type.replace('Entries', '')}` : type
    const selectedAbbr = type === 'address' || type.includes('Entries') ? type : `${type}Address`
    if ( selected ) {
      const selectedSuggestion = type === 'address' ? homeAddressSuggestions[selected.value] :
        ( type === 'addressEntries' ? homeEntries[selected.value] :
          type === 'billing' ? billingAddressSuggestions[selected.value] : billingEntries[selected.value]
        )

      const setEntry = type === 'address' || type === 'addressEntries' ? setHomeEntries : setBillingEntries

      if (selectedSuggestion.entries > 1) {
        queryAutocompleteForEntries(formatAutocompleteSuggestion(selectedSuggestion), setEntry)

        setFieldValue(selectedAbbr, selected)
        setFieldValue(`${abbr === 'address'  ? 'addressEntries' : 'billingEntries'}`, null)
        setFieldValue(`${abbr === 'address'  ? 'address1' : `${abbr}Address1`}`, selectedSuggestion.streetLine)
        setFieldValue(`${abbr === 'address' ? 'address2' : `${abbr}Address2`}`, null)
        setFieldValue(`${abbr === 'address' ? 'city' : `${abbr}City`}`, selectedSuggestion.city)
        setFieldValue(`${abbr === 'address' ? 'state' : `${abbr}State`}`, selectedSuggestion.state)
        setFieldValue(`${abbr === 'address' ? 'zipCode' : `${abbr}ZipCode`}`, selectedSuggestion.zipcode)
      } else {
        if ( !type.includes('Entries') ) {
          setEntry([])
          setFieldValue(`${abbr === 'address'  ? 'addressEntries' : 'billingEntries'}`, undefined)
        }
        setFieldValue(selectedAbbr, selected)
        setFieldValue(`${abbr === 'address' ? 'address1' : `${abbr}Address1`}`, selectedSuggestion.streetLine)
        setFieldValue(`${abbr === 'address' ? 'address2' : `${abbr}Address2`}`, selectedSuggestion.secondary)
        setFieldValue(`${abbr === 'address' ? 'city' : `${abbr}City`}`, selectedSuggestion.city)
        setFieldValue(`${abbr === 'address' ? 'state' : `${abbr}State`}`,   selectedSuggestion.state)
        setFieldValue(`${abbr === 'address' ? 'zipCode' : `${abbr}ZipCode`}`, selectedSuggestion.zipcode)
      }
      setSmartyError(null)
    } else {
      setFieldValue(selectedAbbr, null)
      setFieldValue(`${abbr === 'address' ? 'address1' : `${abbr}Address1`}`, null)
      setFieldValue(`${abbr === 'address' ? 'address2' : `${abbr}Address2`}`, null)
      setFieldValue(`${abbr === 'address' ? 'city' : `${abbr}City`}`, null)
      setFieldValue(`${abbr === 'address' ? 'state' : `${abbr}State`}`, null)
      setFieldValue(`${abbr === 'address' ? 'zipCode' : `${abbr}ZipCode`}`, null)
    }
    setTimeout(() => setFieldTouched(selectedAbbr, true))
  }

  return (
    <div>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={ applicationType === 'non-residential' ? NonResidentialGeneralInfoSchema : ResidentialGeneralInfoSchema}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false)
          onSubmit(values)
        }}
      >
        {({ errors, values, handleChange, handleSubmit, setFieldValue, setFieldTouched, validateForm }) => {
          const handleNextClicked = async () => {
            const foundErrors = await validateForm()
            if (isEmpty(foundErrors)) {
              // Validate US Address using Smarty
              const promiseArray = []
              
              if ( values.address1 && !values.overrideAddressValidation ) {
                promiseArray.push(validateUSAddress(values, 'address1', 'address2', 'city', 'state', 'zipCode', 'address'))
              }
              if ( values.billingAddress1 && !values.billingOverrideAddressValidation ) {
                promiseArray.push(validateUSAddress(values, 'billingAddress1', 'billingAddress2', 'billingCity', 'billingState', 'billingZipCode', 'billing'))
              }

              if ( promiseArray.length > 0 &&  activeStep === 0 ) {
                const result: any = await Promise.all(promiseArray)
                const hasError = result.filter(element => !!element.error)

                if ( hasError.length === 0 ) {
                  const homeResult = result.filter(element => element.type === 'address')
                  const billingResult = result.filter(element => element.type === 'billing')

                  const homeValues = homeResult.length > 0 ? { 
                    address1: homeResult[0].address1,
                    address2: homeResult[0].address2,
                    city: homeResult[0].city,
                    state: homeResult[0].state,
                    zipCode: homeResult[0].zipCode
                  } : { }
                  const billingValues = billingResult.length > 0 ? {
                    billingAddress1: billingResult[0].billingAddress1,
                    billingAddress2: billingResult[0].billingAddress2,
                    billingCity: billingResult[0].billingCity,
                    billingState: billingResult[0].billingState,
                    billingZipCode: billingResult[0].billingZipCode
                  } : { }

                  handleNext({
                    ...values,
                    ...homeValues,
                    ...billingValues
                  })
                  setSmartyError(null)
                } else {
                  setSmartyError(hasError)
                }
              } else {
                handleNext(values)
              }
            }
          }

          const handleNA = (event: any) => {
            const name = event.target.name.replace('NA', '')
            if (!event.target.checked) {
              setFieldValue(`${name}Start`, null)
              setFieldValue(`${name}End`, null)

              setTimeout(() => setFieldTouched(name || '', true))
            }
            
            setFieldValue(`${name}NA`, event.target.checked)
          }

          return (
            <Form noValidate onSubmit={handleSubmit}>
              <Card>
                <Card.Body style={{ background: '#f7f7f7' }}>
                  <Card.Title>Corporate Details</Card.Title>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Agency Name or Doing Business As (DBA)</Form.Label>
                      <Form.Control 
                        type='text'
                        name='agencyName'
                        value={values.agencyName || ''}
                        isInvalid={!!errors.agencyName}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.agencyName}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Legal Entity (as it appears on W9, if applicable)</Form.Label>
                      <Form.Control 
                        type='text'
                        name='legalEntity'
                        value={values.legalEntity || ''}
                        isInvalid={!!errors.legalEntity}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.legalEntity}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={12}>
                      <Form.Label>Service Type(s)</Form.Label>
                      <Select
                        placeholder='Search for service type'
                        options={formatOptions(sortArrayOfObjects(serviceTypes, 'name'), 'serviceTypeId')}
                        value={values.serviceTypes ? formatOptions(serviceTypes, 'serviceTypeId').filter((option)=> !!values.serviceTypes.find(item => item === option.value)) : []}
                        onChange={(values: any) => setFieldValue('serviceTypes', values ? values.map((value: any) => value.value) : [])}
                        isClearable
                        isMulti
                        isDisabled={readonly}
                        styles={errors.serviceTypes ? customStyles : {}}
                      />
                      {errors.serviceTypes ? (
                        <Form.Text className='form-error'>
                          {errors.serviceTypes}
                        </Form.Text>
                      ) : null}
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <AsyncSelect
                          isClearable
                          isDisabled={readonly}
                          name='address'
                          placeholder='Type Address'
                          defaultOptions={homeDefaultOptions}
                          loadOptions={debounce(loadHomeOptions, 750)}
                          value={values.address}
                          onChange={(selected: any) => setSelectedOptions(selected, 'address', setFieldValue, setFieldTouched)}
                          styles={errors.address ? customStyles : {}}
                        />
                        {errors.address ? (
                          <Form.Text className='form-error'>
                            {errors.address}
                          </Form.Text>
                        ) : null}
                      </Form.Group>
                    </Col>
                  </Row>
                  {
                    homeEntries && homeEntries.length > 0 && (
                      <Row>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: 'bold' }}>Additional entries</Form.Label>
                            <Select
                              ref={homeInputElement}
                              isClearable
                              isDisabled={readonly}
                              name='addressEntries'
                              placeholder='Type Address'
                              options={homeEntries.map((suggestion, index) => ({ value: index, label: suggestion.secondary }))}
                              value={values.addressEntries}
                              onChange={(selected: any) => setSelectedOptions(selected, 'addressEntries', setFieldValue, setFieldTouched)}
                              styles={errors.address ? customStyles : {}}
                            />
                          </Form.Group>
                          { errors.addressEntries ? (
                            <Form.Text className='form-error'>
                              {errors.addressEntries}
                            </Form.Text>
                          ) : null}
                        </Col>
                      </Row>
                    )
                  }
                  <Form.Check
                    style={{ marginLeft: 5, fontSize: 12, marginTop: 15 }}
                    inline
                    checked={!!values.overrideAddressValidation}
                    type='checkbox'
                    label='Override Address Validation'
                    name='overrideAddressValidation'
                    onChange={(event) => setFieldValue('overrideAddressValidation', event.target.checked)}
                  />
                  <hr style={{ marginBottom: 5, marginTop: 5 }}/>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Address 1</Form.Label>
                      <Form.Control 
                        type='text'
                        name='address1'
                        value={values.address1 || ''}
                        isInvalid={!!errors.address1 || ( smartyError && !!smartyError.find(error => error.error.type === 'address') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.address1 || ( smartyError ? smartyError.find(error => error.error.type === 'address')?.error.text : '' )}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Address 2</Form.Label>
                      <Form.Control 
                        type='text'
                        name='address2'
                        value={values.address2 || ''}
                        isInvalid={!!errors.address2 || ( smartyError && !!smartyError.find(error => error.error.type === 'address') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.address2}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>City</Form.Label>
                      <Form.Control 
                        type='text'
                        name='city'
                        value={values.city || ''}
                        isInvalid={!!errors.city || ( smartyError && !!smartyError.find(error => error.error.type === 'address') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.city}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>State</Form.Label>
                      <Form.Control 
                        type='text'
                        name='state'
                        value={values.state || ''}
                        isInvalid={!!errors.state || ( smartyError && !!smartyError.find(error => error.error.type === 'address') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.state}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Zip</Form.Label>
                      <Form.Control 
                        type='text'
                        name='zipCode'
                        placeholder='#####-####'
                        value={values.zipCode || ''}
                        isInvalid={!!errors.zipCode || ( smartyError && !!smartyError.find(error => error.error.type === 'address') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.zipCode}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Contact First Name</Form.Label>
                      <Form.Control 
                        type='text'
                        name='contactFirstName'
                        value={values.contactFirstName || ''}
                        isInvalid={!!errors.contactFirstName}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.contactFirstName}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Contact Last Name</Form.Label>
                      <Form.Control 
                        type='text'
                        name='contactLastName'
                        value={values.contactLastName || ''}
                        isInvalid={!!errors.contactLastName}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.contactLastName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control 
                        type='text'
                        name='emailAddress'
                        value={values.emailAddress || ''}
                        isInvalid={!!errors.emailAddress}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.emailAddress}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Website URL</Form.Label>
                      <Form.Control 
                        type='text'
                        name='website'
                        value={values.website || ''}
                        isInvalid={!!errors.website}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.website}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={4} md={4} sm={6} xs={6}>
                      <Form.Label>Phone Number</Form.Label>
                      <Form.Control 
                        as={MaskedInput}
                        name='phoneNumber'
                        mask='(111) 111-1111'
                        value={values.phoneNumber || ''}
                        isInvalid={!!errors.phoneNumber}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.phoneNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={2} md={2} sm={6} xs={6}>
                      <Form.Label>Extension</Form.Label>
                      <Form.Control 
                        type='text'
                        name='extension'
                        value={values.extension || ''}
                        isInvalid={!!errors.extension}
                        onChange={handleChange}
                        disabled={readonly}
                        maxLength={4}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.extension}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Fax</Form.Label>
                      <Form.Control 
                        type='text'
                        name='faxNumber'
                        value={values.faxNumber || ''}
                        isInvalid={!!errors.faxNumber}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.faxNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
              <Card style={{ marginTop: 25 }}>
                <Card.Body style={{ background: '#f7f7f7' }}>
                  <Card.Title>Billing Information</Card.Title>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Tax ID</Form.Label>
                      <Form.Control 
                        type='text'
                        name='taxIdNumber'
                        value={values.taxIdNumber || ''}
                        isInvalid={!!errors.taxIdNumber}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.taxIdNumber}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Tax ID Type</Form.Label>
                      <Form.Control 
                        as='select'
                        name='qualifier'
                        value={values.qualifier || ''}
                        isInvalid={!!errors.qualifier}
                        onChange={handleChange}
                        disabled={readonly}
                      >
                        <option></option>
                        <option value={24}>EIN</option>
                        <option value={34}>SSN</option>
                      </Form.Control>
                      <Form.Control.Feedback type='invalid'>
                        {errors.qualifier}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>NPI No.</Form.Label>
                      <Form.Control 
                        type='text'
                        name='npi'
                        value={values.npi || ''}
                        isInvalid={!!errors.npi}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.npi}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>WI Medicaid No.</Form.Label>
                      <Form.Control 
                        type='text'
                        name='medicaidId'
                        value={values.medicaidId || ''}
                        isInvalid={!!errors.medicaidId}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.medicaidId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Medicare No.</Form.Label>
                      <Form.Control 
                        type='text'
                        name='medicareId'
                        value={values.medicareId || ''}
                        isInvalid={!!errors.medicareId}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.medicareId}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Alternate ID (Provider Agency ID)</Form.Label>
                      <Form.Control 
                        type='text'
                        name='providerAgencyId'
                        value={values.providerAgencyId || ''}
                        isInvalid={!!errors.providerAgencyId}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.providerAgencyId}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Billing Company Name</Form.Label>
                      <Form.Control 
                        type='text'
                        name='billingName'
                        value={values.billingName || ''}
                        isInvalid={!!errors.billingName}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.billingName}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Billing Company Address</Form.Label>
                        <AsyncSelect
                          isClearable
                          isDisabled={readonly}
                          name='billingAddress'
                          placeholder='Type Address'
                          defaultOptions={billingDefaultOptions}
                          loadOptions={debounce(loadBillingOptions, 750)}
                          value={values.billingAddress}
                          onChange={(selected: any) => setSelectedOptions(selected, 'billing', setFieldValue, setFieldTouched)}
                          styles={errors.billingAddress ? customStyles : {}}
                        />
                        {errors.billingAddress ? (
                          <Form.Text className='form-error'>
                            {errors.billingAddress}
                          </Form.Text>
                        ) : null}
                      </Form.Group>
                    </Col>
                  </Row>
                  {
                    billingEntries && billingEntries.length > 0 && (
                      <Row>
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label style={{ fontWeight: 'bold' }}>Additional entries</Form.Label>
                            <Select
                              ref={billingInputElement}
                              isClearable
                              isDisabled={readonly}
                              name='billingEntries'
                              placeholder='Type Address'
                              options={billingEntries.map((suggestion, index) => ({ value: index, label: suggestion.secondary }))}
                              value={values.billingEntries}
                              onChange={(selected: any) => setSelectedOptions(selected, 'billingEntries', setFieldValue, setFieldTouched)}
                              styles={errors.billingEntries ? customStyles : {}}
                            />
                            { errors.billingEntries ? (
                              <Form.Text className='form-error'>
                                {errors.billingEntries}
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                        </Col>
                      </Row>
                    )
                  }
                  <Form.Check
                    style={{ marginLeft: 5, fontSize: 12, marginTop: 15 }}
                    inline
                    checked={!!values.billingOverrideAddressValidation}
                    type='checkbox'
                    label='Override Address Validation'
                    name='billingOverrideAddressValidation'
                    onChange={(event) => setFieldValue('billingOverrideAddressValidation', event.target.checked)}
                  />
                  <hr style={{ marginBottom: 5, marginTop: 5 }}/>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Billing Company Address 1</Form.Label>
                      <Form.Control 
                        type='text'
                        name='billingAddress1'
                        value={values.billingAddress1 || ''}
                        isInvalid={!!errors.billingAddress1 || ( smartyError && !!smartyError.find(error => error.error.type === 'billing') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.billingAddress1 || ( smartyError ? smartyError.find(error => error.error.type === 'billing')?.error.text : '' )}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Billing Company Address 2</Form.Label>
                      <Form.Control 
                        type='text'
                        name='billingAddress2'
                        value={values.billingAddress2 || ''}
                        isInvalid={!!errors.billingAddress2 || ( smartyError && !!smartyError.find(error => error.error.type === 'billing') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.billingAddress2}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Billing Company City</Form.Label>
                      <Form.Control 
                        type='text'
                        name='billingCity'
                        value={values.billingCity || ''}
                        isInvalid={!!errors.billingCity || ( smartyError && !!smartyError.find(error => error.error.type === 'billing') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.billingCity}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Billing Company State</Form.Label>
                      <Form.Control 
                        type='text'
                        name='billingState'
                        value={values.billingState || ''}
                        isInvalid={!!errors.billingState || ( smartyError && !!smartyError.find(error => error.error.type === 'billing') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.billingState}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Billing Company Zip</Form.Label>
                      <Form.Control 
                        type='text'
                        name='billingZipCode'
                        placeholder='#####-####'
                        value={values.billingZipCode || ''}
                        isInvalid={!!errors.billingZipCode || ( smartyError && !!smartyError.find(error => error.error.type === 'billing') )}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.billingZipCode}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
              <Card style={{ marginTop: 25 }}>
                <Card.Body style={{ background: '#f7f7f7' }}>
                  <Card.Title>Additional Info</Card.Title>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Is this a new business (within the last 24 months)</Form.Label>
                      <div style={{ display: 'flex' }}>
                        <Form.Check
                          type='radio'
                          label='Yes'
                          name='newBusiness'
                          id='yesNewBuisness'
                          checked={values.newBusiness === 'YES'}
                          onChange={() => {
                            setFieldValue('newBusiness', 'YES')
                            setFieldValue('experienceYears', '')
                          }}
                          disabled={readonly}
                        />
                        <Form.Check
                          style={{ marginLeft: '15px' }}
                          type='radio'
                          label='No'
                          name='newBusiness'
                          id='noNewBusiness'
                          checked={values.newBusiness === 'NO'}
                          onChange={() => {
                            setFieldValue('newBusiness', 'NO')
                            setFieldValue('relevantExperience', '')
                          }}
                          disabled={readonly}
                        />
                      </div>
                      <Form.Control.Feedback type='invalid'>
                        {errors.newBusiness}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Years of Relevant Experience (if NO in previous)</Form.Label>
                      <Form.Control 
                        type='text'
                        name='experienceYears'
                        value={values.experienceYears}
                        isInvalid={!!errors.experienceYears}
                        onChange={handleChange}
                        disabled={values.newBusiness === 'YES'||readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.experienceYears}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={12}>
                      <Form.Label>Relevant Experience (if Yes in previous)</Form.Label>
                      <Form.Control 
                        as='textarea'
                        name='relevantExperience'
                        rows={4}
                        value={values.relevantExperience || ''}
                        isInvalid={!!errors.relevantExperience}
                        onChange={handleChange}
                        disabled={values.newBusiness === 'NO'||readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.relevantExperience}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={12}>
                      <Form.Label>Target Groups Served</Form.Label>
                      <Select
                        placeholder='Search for target groups'
                        options={formatOptions(sortArrayOfObjects(targetGroups, 'name'), 'providerTargetGroupId')}
                        value={values.targetGroups ? formatOptions(targetGroups, 'providerTargetGroupId').filter((option)=> !!values.targetGroups.find(item => item === option.value)) : []}
                        onChange={(values: any) => setFieldValue('targetGroups', values ? values.map((value: any) => value.value) : [])}
                        isClearable
                        isMulti
                        isDisabled={readonly}
                        styles={errors.targetGroups ? customStyles : {}}
                      />
                      {errors.targetGroups ? (
                        <Form.Text className='form-error'>
                          {errors.targetGroups}
                        </Form.Text>
                      ) : null}
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                      <Form.Label>Provider Features</Form.Label>
                      <Card className={errors.accessibility ? 'card-error' : ''}>
                        <Card.Body>
                          <Row>
                            {
                              formatOptions(sortArrayOfObjects(accessibilities, 'name'), 'accessibilityId').map((option) => {
                                return (
                                  <Col key={option.value} xs={6}>
                                    <Form.Check 
                                      type='checkbox' 
                                      label={option.label} 
                                      checked={values.accessibility.includes(option.value)}
                                      onChange={() => handleCheckboxGroupChange('accessibility', option.value, values, setFieldValue)}
                                      disabled={readonly}
                                    />
                                  </Col>
                                )
                              })
                            }
                          </Row>
                        </Card.Body> 
                      </Card>
                      {errors.accessibility ? (
                        <Form.Text className='form-error'>
                          {errors.accessibility}
                        </Form.Text>
                      ) : null}
                    </Form.Group>
                    <Col lg={6} md={6} sm={12} xs={12}>
                      <Row>
                        <Form.Group as={Col} lg={12}>
                          <Form.Label>Overnight Care</Form.Label>
                          <Select
                            isClearable
                            name='overnightCareId'
                            placeholder='Type Overnight Care Option'
                            options={formatOptions(sortArrayOfObjects(overnightCare, 'name'), 'overnightCareId')}
                            value={values.overnightCareId ? formatOptions(sortArrayOfObjects(overnightCare, 'name'), 'overnightCareId').find(overnightCare => overnightCare.value === values.overnightCareId) : null}
                            onChange={(values: any) => setFieldValue('overnightCareId', values ? values.value : null)}
                            isDisabled={readonly}
                            styles={errors.overnightCareId ? customStyles : {}}
                          />
                          {errors.overnightCareId ? (
                            <Form.Text className='form-error'>
                              {errors.overnightCareId}
                            </Form.Text>
                          ) : null}
                        </Form.Group>
                      </Row>
                      <Row>
                        <Form.Group as={Col} lg={12}>
                          <Form.Label>Staffing Model</Form.Label>
                          <Select
                            isClearable
                            name='staffingModelId'
                            placeholder='Type Staffing Model Option'
                            options={formatOptions(sortArrayOfObjects(staffingModel, 'name'), 'staffingModelId')}
                            value={values.staffingModelId ? formatOptions(sortArrayOfObjects(staffingModel, 'name'), 'staffingModelId').find(overnightCare => overnightCare.value === values.staffingModelId) : null}
                            onChange={(values: any) => setFieldValue('staffingModelId', values ? values.value : null)}
                            isDisabled={readonly}
                            styles={errors.staffingModelId ? customStyles : {}}
                          />
                          {errors.staffingModelId ? (
                            <Form.Text className='form-error'>
                              {errors.staffingModelId}
                            </Form.Text>
                          ) : null}
                        </Form.Group>
                      </Row>
                      <Row>
                        <Form.Group as={Col} lg={12}>
                          <Form.Label>Handicapped Accessible</Form.Label>
                          <Row style={{ marginLeft: 5 }}>
                            No
                            <LightBlueSwitch
                              name='handicappedAccessible'
                              checked={values.handicappedAccessible}
                              onChange={handleChange}
                              inputProps={{ 'aria-label': 'exclude' }}
                              disabled={readonly}
                            />
                            Yes
                          </Row>
                          {errors.handicappedAccessible ? (
                            <Form.Text className='form-error'>
                              {errors.handicappedAccessible}
                            </Form.Text>
                          ) : null}
                        </Form.Group>
                      </Row>
                      <Row>
                        <Form.Group as={Col} lg={12}>
                          <Form.Label>Do you provide Equity and Inclusion Training to your employees?</Form.Label>
                          <Row style={{ marginLeft: 5 }}>
                            No
                            <LightBlueSwitch
                              name='equityInclusionTraining'
                              checked={values.equityInclusionTraining}
                              onChange={handleChange}
                              inputProps={{ 'aria-label': 'exclude' }}
                              disabled={readonly}
                            />
                            Yes
                          </Row>
                          {errors.equityInclusionTraining ? (
                            <Form.Text className='form-error'>
                              {errors.equityInclusionTraining}
                            </Form.Text>
                          ) : null}
                        </Form.Group>
                      </Row>
                    </Col>
                  </Row>
                  {
                    applicationType === 'non-residential' ? (
                      <>
                        <Row>
                          <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                            <Form.Label>Gender Served</Form.Label>
                            <Card className={errors.genderServed ? 'card-error' : ''}>
                              <Card.Body>
                                <Form.Check 
                                  type='radio'
                                  name='genderServed'
                                  label='Female Only'
                                  checked={values.genderServed.includes('Female Only')}
                                  onChange={() => setFieldValue('genderServed', 'Female Only')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio' 
                                  name='genderServed'
                                  label='Male Only'
                                  checked={values.genderServed.includes('Male Only')}
                                  onChange={() => setFieldValue('genderServed', 'Male Only')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='genderServed'
                                  label='Male & Female'
                                  checked={values.genderServed.includes('Male & Female')}
                                  onChange={() => setFieldValue('genderServed', 'Male & Female')}
                                  disabled={readonly}
                                />
                              </Card.Body> 
                            </Card>
                            {errors.genderServed ? (
                              <Form.Text className='form-error'>
                                {errors.genderServed}
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                          <Col lg={6} md={6} sm={12} xs={12}>
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Languages Spoken</Form.Label>
                              <Select
                                placeholder='Search for language'
                                options={formatOptions(sortArrayOfObjects(languages, 'name'), 'languageId')}
                                value={values.languages ? formatOptions(languages, 'languageId').filter((option)=> !!values.languages.find(item => item === option.value)) : []}
                                onChange={(values: any) => setFieldValue('languages', values ? values.map((value: any) => value.value) : [])}
                                isClearable
                                isMulti
                                isDisabled={readonly}
                                styles={errors.languages ? customStyles : {}}
                              />
                              {errors.languages ? (
                                <Form.Text className='form-error'>
                                  {errors.languages}
                                </Form.Text>
                              ) : null}
                            </Form.Group>
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Race</Form.Label>
                              <Select
                                isClearable
                                isDisabled={readonly}
                                name='raceId'
                                placeholder='Search for race'
                                options={formatOptions(race, 'race_id')}
                                value={values.raceId ? formatOptions(race, 'race_id').find(opt => opt.value === values.raceId) : null}
                                onChange={(values: any) => setFieldValue('raceId', values ? values.value : null)}
                                styles={errors.raceId ? customStyles : {}}
                              />
                              { errors.raceId ? (
                                <Form.Text className='form-error'>
                                  {errors.raceId}
                                </Form.Text>
                              ) : null}
                            </Form.Group>
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Ethnicity</Form.Label>
                              <Select
                                isClearable
                                isDisabled={readonly}
                                name='ethnicityId'
                                placeholder='Search for ethnicity'
                                options={formatOptions(ethnicity, 'ethnicity_id')}
                                value={values.ethnicityId ? formatOptions(ethnicity, 'ethnicity_id').find(opt => opt.value === values.ethnicityId) : null}
                                onChange={(values: any) => setFieldValue('ethnicityId', values ? values.value : null)}
                                styles={errors.ethnicityId ? customStyles : {}}
                              />
                              { errors.ethnicityId ? (
                                <Form.Text className='form-error'>
                                  {errors.ethnicityId}
                                </Form.Text>
                              ) : null}
                            </Form.Group>  
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Publish Race/Ethnicity on Provider Directory</Form.Label>
                              <Row style={{ marginLeft: 5 }}>
                                No
                                <LightBlueSwitch
                                  name='publishRaceEthnicity'
                                  checked={values.publishRaceEthnicity}
                                  onChange={handleChange}
                                  inputProps={{ 'aria-label': 'exclude' }}
                                  disabled={readonly}
                                />
                                  Yes
                              </Row>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                            <Form.Label>Home & Community Based Wavier Compliant</Form.Label>
                            <Card className={errors.wavierCompliant ? 'card-error' : ''}>
                              <Card.Body>
                                {
                                  formatOptions(sortArrayOfObjects(wavierCompliant, 'name').filter(filteredWavier => filteredWavier.type === 'non-residential'), 'homeCommunityCompliantId')
                                    .sort((x,y) => toLower(x.label) === 'n/a' ? -1 : toLower(y.label) === 'n/a' ? 1 : 0)
                                    .map((option) => {
                                      return (
                                        <Form.Check 
                                          key={option.value}
                                          type='checkbox'
                                          label={option.label} 
                                          checked={values.wavierCompliant.includes(option.value)}
                                          onChange={() => handleCheckboxGroupChange('wavierCompliant', option.value, values, setFieldValue)}
                                          disabled={readonly}
                                        />
                                      )
                                    })
                                }
                              </Card.Body> 
                            </Card>
                            {errors.wavierCompliant ? (
                              <Form.Text className='form-error'>
                                {errors.wavierCompliant}
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                          <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                            <Form.Label>Service Details</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={4}
                              placeholder='Please provide a brief description of your service details: (i.e., Memory Care, Behavioral Health, etc.)'
                              name='serviceDetails'
                              isInvalid={!!errors.serviceDetails}
                              value={values.serviceDetails}
                              onChange={handleChange}
                              disabled={readonly}
                            />
                            <Form.Control.Feedback type='invalid'>
                              {errors.serviceDetails}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                      </>
                    ) : (
                      <>
                        <Row>
                          <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                            <Form.Label>Residential Provider Applicants</Form.Label>
                            <Card className={errors.residentialProviderApplicants ? 'card-error' : ''}>
                              <Card.Body>
                                <Form.Check 
                                  type='radio' 
                                  name='residentialProviderApplicants'
                                  label='1-2 Bed Owner Occupied'
                                  checked={values.residentialProviderApplicants.includes('1-2 Bed Owner Occupied')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', '1-2 Bed Owner Occupied')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='residentialProviderApplicants'
                                  label='1-2 Bed Corporate'
                                  checked={values.residentialProviderApplicants.includes('1-2 Bed Corporate')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', '1-2 Bed Corporate')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='residentialProviderApplicants'
                                  label='3-4 Bed Owner Occupied'
                                  checked={values.residentialProviderApplicants.includes('3-4 Bed Owner Occupied')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', '3-4 Bed Owner Occupied')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio' 
                                  name='residentialProviderApplicants'
                                  label='3-4 Bed Corporate'
                                  checked={values.residentialProviderApplicants.includes('3-4 Bed Corporate')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', '3-4 Bed Corporate')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='residentialProviderApplicants'
                                  label='5-8 Bed'
                                  checked={values.residentialProviderApplicants.includes('5-8 Bed')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', '5-8 Bed')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='residentialProviderApplicants'
                                  label='9 Bed & Over'
                                  checked={values.residentialProviderApplicants.includes('9 Bed & Over')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', '9 Bed & Over')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='residentialProviderApplicants'
                                  label='Residential Care Apartment Complex'
                                  checked={values.residentialProviderApplicants && values.residentialProviderApplicants.includes('Residential Care Apartment Complex')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', 'Residential Care Apartment Complex')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='residentialProviderApplicants'
                                  label='Skilled Nursing Facility'
                                  checked={values.residentialProviderApplicants && values.residentialProviderApplicants.includes('Skilled Nursing Facility')}
                                  onChange={() => setFieldValue('residentialProviderApplicants', 'Skilled Nursing Facility')}
                                  disabled={readonly}
                                />
                              </Card.Body> 
                            </Card>
                            {errors.residentialProviderApplicants ? (
                              <Form.Text className='form-error'>
                                {errors.residentialProviderApplicants}
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                          <Col lg={6} md={6} sm={12} xs={12}>
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Languages Spoken</Form.Label>
                              <Select
                                placeholder='Search for language'
                                options={formatOptions(sortArrayOfObjects(languages, 'name'), 'languageId')}
                                value={values.languages ? formatOptions(languages, 'languageId').filter((option)=> !!values.languages.find(item => item === option.value)) : []}
                                onChange={(values: any) => setFieldValue('languages', values ? values.map((value: any) => value.value) : [])}
                                isClearable
                                isMulti
                                isDisabled={readonly}
                                styles={errors.languages ? customStyles : {}}
                              />
                              {errors.languages ? (
                                <Form.Text className='form-error'>
                                  {errors.languages}
                                </Form.Text>
                              ) : null}
                            </Form.Group>
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Race</Form.Label>
                              <Select
                                isClearable
                                isDisabled={readonly}
                                name='raceId'
                                placeholder='Search for race'
                                options={formatOptions(race, 'race_id')}
                                value={values.raceId ? formatOptions(race, 'race_id').find(opt => opt.value === values.raceId) : null}
                                onChange={(values: any) => setFieldValue('raceId', values ? values.value : null)}
                                styles={errors.raceId ? customStyles : {}}
                              />
                            </Form.Group>
                            { errors.raceId ? (
                              <Form.Text className='form-error'>
                                {errors.raceId}
                              </Form.Text>
                            ) : null}
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Ethnicity</Form.Label>
                              <Select
                                isClearable
                                isDisabled={readonly}
                                name='ethnicityId'
                                placeholder='Search for ethnicity'
                                options={formatOptions(ethnicity, 'ethnicity_id')}
                                value={values.ethnicityId ? formatOptions(ethnicity, 'ethnicity_id').find(opt => opt.value === values.ethnicityId) : null}
                                onChange={(values: any) => setFieldValue('ethnicityId', values ? values.value : null)}
                                styles={errors.ethnicityId ? customStyles : {}}
                              />
                            </Form.Group>  
                            <Form.Group as={Col} lg={12}>
                              <Form.Label>Publish Race/Ethnicity on Provider Directory</Form.Label>
                              <Row style={{ marginLeft: 5 }}>
                                No
                                <LightBlueSwitch
                                  name='publishRaceEthnicity'
                                  checked={values.publishRaceEthnicity}
                                  onChange={handleChange}
                                  inputProps={{ 'aria-label': 'exclude' }}
                                  disabled={readonly}
                                />
                                  Yes
                              </Row>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row>
                          <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                            <Form.Label>Home & Community Based Wavier Compliant</Form.Label>
                            <Card className={errors.wavierCompliant ? 'card-error' : ''}>
                              <Card.Body>
                                {
                                  formatOptions(sortArrayOfObjects(wavierCompliant, 'name').filter(filteredWavier => filteredWavier.name === 'N/A' || filteredWavier.type === 'residential'), 'homeCommunityCompliantId')
                                    .sort((x,y) => toLower(x.label) === 'n/a' ? -1 : toLower(y.label) === 'n/a' ? 1 : 0).map((option) => {
                                      return (
                                        <Form.Check 
                                          key={option.value}
                                          type='checkbox'
                                          label={option.label} 
                                          checked={values.wavierCompliant.includes(option.value)}
                                          onChange={() => handleCheckboxGroupChange('wavierCompliant', option.value, values, setFieldValue)}
                                          disabled={readonly}
                                        />
                                      )
                                    })
                                }
                              </Card.Body> 
                            </Card>
                            {errors.wavierCompliant ? (
                              <Form.Text className='form-error'>
                                {errors.wavierCompliant}
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                          <Form.Group as={Col} lg={6} md={6} sm={12} xs={12}>
                            <Form.Label>Gender Served</Form.Label>
                            <Card className={errors.genderServed ? 'card-error' : ''}>
                              <Card.Body>
                                <Form.Check 
                                  type='radio'
                                  name='genderServed'
                                  label='Female Only'
                                  checked={values.genderServed.includes('Female Only')}
                                  onChange={() => setFieldValue('genderServed', 'Female Only')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio' 
                                  name='genderServed'
                                  label='Male Only'
                                  checked={values.genderServed.includes('Male Only')}
                                  onChange={() => setFieldValue('genderServed', 'Male Only')}
                                  disabled={readonly}
                                />
                                <Form.Check 
                                  type='radio'
                                  name='genderServed'
                                  label='Male & Female'
                                  checked={values.genderServed.includes('Male & Female')}
                                  onChange={() => setFieldValue('genderServed', 'Male & Female')}
                                  disabled={readonly}
                                />
                              </Card.Body> 
                            </Card>
                            {errors.genderServed ? (
                              <Form.Text className='form-error'>
                                {errors.genderServed}
                              </Form.Text>
                            ) : null}
                          </Form.Group>
                        </Row>
                      </>
                    )
                  }
                  <Row>
                    <Form.Group as={Col} lg={6} sm={12}>
                      <Form.Label>Counties Served</Form.Label>
                      <Select
                        placeholder='Search for County'
                        options={formatOptions(counties, 'countyId')}
                        value={values.counties ? formatOptions(counties, 'countyId').filter((option)=> !!values.counties.find(item => item === option.value)) : []}
                        onChange={(currentValues: any) => handleCountyChange(true, currentValues, values.countiesNotServed, setFieldValue)}
                        isClearable
                        isMulti
                        isDisabled={readonly}
                        styles={errors.counties ? customStyles : {}}
                      />
                      {errors.counties ? (
                        <Form.Text className='form-error'>
                          {errors.counties}
                        </Form.Text>
                      ) : null}
                    </Form.Group>
                    <Form.Group as={Col} lg={6} sm={12}>
                      <Form.Label>Licensed Capacity</Form.Label>
                      <Form.Control 
                        type='text'
                        name='licensedCapacity'
                        value={values.licensedCapacity}
                        isInvalid={!!errors.licensedCapacity}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.licensedCapacity}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
              <Card style={{ marginTop: 25 }}>
                <Card.Body style={{ background: '#f7f7f7' }}>
                  <Card.Title>Hours of Operation</Card.Title>
                  <Row>
                    <Col xs={4} className='operationHoursCol1'>
                      <Form.Group>
                        <Form.Label>Days of Week</Form.Label>
                        <Form.Check 
                          name='monday'
                          type='checkbox'
                          label='Monday'
                          checked={values.monday || false}
                          isInvalid={!!errors.monday}
                          feedback={errors.monday}
                          onChange={handleChange}
                          disabled={readonly}
                          className='operationHoursTitle'
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Check
                          name='mondayNA' 
                          type='checkbox' 
                          label='N/A'
                          checked={values.mondayNA || false}
                          onChange={handleNA}
                          isInvalid={!!errors.mondayNA}
                          feedback={errors.mondayNA}
                          disabled={readonly}
                          className='operationHoursNA1'
                        />
                      </Form.Group>
                    </Col>
                    <Form.Group as={Col} xs={3} style={{ marginTop: 12 }}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          label='Start Time'
                          mask='__:__ _M'
                          error={!!errors.mondayStart}
                          helperText={errors.mondayStart}
                          value={values.mondayStart}
                          onChange={value => setFieldValue('mondayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                      {copyMondayError ? (
                        <Form.Text className='form-error'>Please fill start and end time to copy.</Form.Text>
                      ) : null}
                    </Form.Group>
                    <Form.Group as={Col} xs={3} style={{ marginTop: 12 }}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          label='End Time'
                          mask='__:__ _M'
                          error={!!errors.mondayEnd}
                          helperText={errors.mondayEnd}
                          value={values.mondayEnd}
                          onChange={value => setFieldValue('mondayEnd', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Col xs={2}>
                      <div className='operation-hours-container' onClick={() => handleCopyToSome('monday', 1, 5, values, setFieldValue, setFieldTouched)}>
                        <FontAwesomeIcon icon={['far', 'copy']} size='lg' className='modal-header-icon' />
                        <span className='operation-hours-text'>Copy to Tuesday - Friday</span>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={4} className='operationHoursCol1'>
                      <Form.Group>
                        <Form.Check 
                          name='tuesday'
                          type='checkbox'
                          label='Tuesday'
                          checked={values.tuesday || false}
                          isInvalid={!!errors.tuesday}
                          feedback={errors.tuesday}
                          onChange={handleChange}
                          disabled={readonly}
                          className='operationHoursTitle'
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Check
                          name='tuesdayNA' 
                          type='checkbox' 
                          label='N/A'
                          checked={values.tuesdayNA || false}
                          onChange={handleNA}
                          isInvalid={!!errors.tuesdayNA}
                          feedback={errors.tuesdayNA}
                          disabled={readonly}
                          className='operationHoursNA'
                        />
                      </Form.Group>
                    </Col>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          mask='__:__ _M'
                          error={!!errors.tuesdayStart}
                          helperText={errors.tuesdayStart}
                          value={values.tuesdayStart}
                          onChange={value => setFieldValue('tuesdayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          mask='__:__ _M'
                          error={!!errors.tuesdayEnd}
                          helperText={errors.tuesdayEnd}
                          value={values.tuesdayEnd}
                          onChange={value => setFieldValue('tuesdayEnd', value)} 
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col xs={4} className='operationHoursCol1'>
                      <Form.Group>
                        <Form.Check 
                          name='wednesday'
                          type='checkbox'
                          label='Wednesday'
                          checked={values.wednesday || false}
                          isInvalid={!!errors.wednesday}
                          feedback={errors.wednesday}
                          onChange={handleChange}
                          disabled={readonly}
                          className='operationHoursTitle'
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Check
                          name='wednesdayNA' 
                          type='checkbox' 
                          label='N/A'
                          checked={values.wednesdayNA || false}
                          onChange={handleNA}
                          isInvalid={!!errors.wednesdayNA}
                          feedback={errors.wednesdayNA}
                          disabled={readonly}
                          className='operationHoursNA'
                        />
                      </Form.Group>
                    </Col>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          mask='__:__ _M'
                          error={!!errors.wednesdayStart}
                          helperText={errors.wednesdayStart}
                          value={values.wednesdayStart}
                          onChange={value => setFieldValue('wednesdayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          mask='__:__ _M'
                          error={!!errors.wednesdayEnd}
                          helperText={errors.wednesdayEnd}
                          value={values.wednesdayEnd}
                          onChange={value => setFieldValue('wednesdayEnd', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col xs={4} className='operationHoursCol1'>
                      <Form.Group>
                        <Form.Check 
                          name='thursday'
                          type='checkbox'
                          label='Thursday'
                          checked={values.thursday || false}
                          isInvalid={!!errors.thursday}
                          feedback={errors.thursday}
                          onChange={handleChange}
                          disabled={readonly}
                          className='operationHoursTitle'
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Check
                          name='thursdayNA' 
                          type='checkbox' 
                          label='N/A'
                          checked={values.thursdayNA || false}
                          onChange={handleNA}
                          isInvalid={!!errors.thursdayNA}
                          feedback={errors.thursdayNA}
                          disabled={readonly}
                          className='operationHoursNA'
                        />
                      </Form.Group>
                    </Col>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          mask='__:__ _M'
                          error={!!errors.thursdayStart}
                          helperText={errors.thursdayStart}
                          value={values.thursdayStart}
                          onChange={value => setFieldValue('thursdayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          mask='__:__ _M'
                          error={!!errors.thursdayEnd}
                          helperText={errors.thursdayEnd}
                          value={values.thursdayEnd}
                          onChange={value => setFieldValue('thursdayEnd', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Col xs={4} className='operationHoursCol1'>
                      <Form.Group>
                        <Form.Check 
                          name='friday'
                          type='checkbox'
                          label='Friday'
                          checked={values.friday || false}
                          isInvalid={!!errors.friday}
                          feedback={errors.friday}
                          onChange={handleChange}
                          disabled={readonly}
                          className='operationHoursTitle'
                        />
                      </Form.Group>
                      <Form.Group>
                        <Form.Check
                          name='fridayNA' 
                          type='checkbox' 
                          label='N/A'
                          checked={values.fridayNA || false}
                          onChange={handleNA}
                          isInvalid={!!errors.fridayNA}
                          feedback={errors.fridayNA}
                          disabled={readonly}
                          className='operationHoursNA'
                        />
                      </Form.Group>
                    </Col>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          mask='__:__ _M'
                          error={!!errors.fridayStart}
                          helperText={errors.fridayStart}
                          value={values.fridayStart}
                          onChange={value => setFieldValue('fridayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          mask='__:__ _M'
                          error={!!errors.fridayEnd}
                          helperText={errors.fridayEnd}
                          value={values.fridayEnd}
                          onChange={value => setFieldValue('fridayEnd', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={4} className='operationHoursCol1'>
                      <Form.Check 
                        name='saturday'
                        type='checkbox'
                        label='Saturday'
                        checked={values.saturday || false}
                        isInvalid={!!errors.saturday}
                        feedback={errors.saturday} 
                        onChange={handleChange}
                        disabled={readonly}
                      />
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          mask='__:__ _M'
                          error={!!errors.saturdayStart}
                          helperText={errors.saturdayStart}
                          value={values.saturdayStart}
                          onChange={value => setFieldValue('saturdayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                      {copySaturdayError ? (
                        <Form.Text className='form-error'> Please fill start and end time to copy.</Form.Text>
                      ) : null}
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          mask='__:__ _M'
                          error={!!errors.saturdayEnd}
                          helperText={errors.saturdayEnd}
                          value={values.saturdayEnd}
                          onChange={value => setFieldValue('saturdayEnd', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Col xs={2}>
                      <div className='operation-hours-saturday-container' onClick={() => handleCopyToSome('saturday', 6, 7, values, setFieldValue, setFieldTouched)}>
                        <FontAwesomeIcon icon={['far', 'copy']} size='lg' className='modal-header-icon' />
                        <span className='operation-hours-text'>Copy to Sunday</span>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={4} className='operationHoursCol1'>
                      <Form.Check 
                        name='sunday'
                        type='checkbox'
                        label='Sunday'
                        checked={values.sunday || false}
                        isInvalid={!!errors.sunday}
                        feedback={errors.sunday}
                        onChange={handleChange}
                        disabled={readonly}
                      />
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker
                          mask='__:__ _M'
                          error={!!errors.sundayStart}
                          helperText={errors.sundayStart}
                          value={values.sundayStart}
                          onChange={value => setFieldValue('sundayStart', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                    <Form.Group as={Col} xs={3}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardTimePicker 
                          mask='__:__ _M'
                          error={!!errors.sundayEnd}
                          helperText={errors.sundayEnd}
                          value={values.sundayEnd}
                          onChange={value => setFieldValue('sundayEnd', value)}
                          disabled={readonly}
                        />
                      </MuiPickersUtilsProvider>
                    </Form.Group>
                  </Row>
                  <Row>
                    <Form.Group as={Col} xs={12}>
                      <Form.Label>Exceptions</Form.Label>
                      <Form.Control 
                        as='textarea'
                        rows={2}
                        name='holiday'
                        value={values.holiday}
                        onChange={handleChange}
                        placeholder='Please list any exceptions (i.e. holidays)'
                        isInvalid={!!errors.holiday}
                        disabled={readonly}
                      />
                      <Form.Control.Feedback type='invalid'>
                        {errors.holiday}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Row>
                </Card.Body>
              </Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}>
                {
                  !readonly ? (
                    <Button
                      type='submit'
                      variant='secondary'
                      style={{ width: '115px'}}
                    >
                    Save & Exit
                    </Button>
                  ) : null
                }
                <div>
                  <Button
                    variant='secondary'
                    style={{ width: '115px', marginRight: 10 }}
                    type='submit'
                    disabled
                  >
                    Previous
                  </Button>
                  <Button
                    variant='info'
                    style={{ width: '115px', marginRight: 10 }}
                    onClick={() => handleNextClicked()}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Form>
          )}}
      </Formik>
    </div>
  )
}

export default GeneralInfo